/**
 * Backup Layer - localStorage 自動備份
 *
 * 提供編輯器內容的自動備份與恢復功能
 * @see Requirements 10.3, 10.4
 */

import type { EditorSelection } from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * 備份資料結構
 */
export interface EditorBackup {
  /** 文件 ID */
  docId: string;
  /** Markdown 內容 */
  markdown: string;
  /** 游標位置 */
  selection: EditorSelection;
  /** 備份時間戳 */
  timestamp: number;
  /** 版本向量 */
  versionVector: number;
}

/**
 * 備份管理器配置
 */
export interface BackupManagerConfig {
  /** 自動備份間隔 (毫秒)，預設 30000 (30 秒) */
  autoSaveIntervalMs: number;
  /** localStorage key 前綴 */
  storageKeyPrefix: string;
  /** 最大備份數量 */
  maxBackups: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_CONFIG: BackupManagerConfig = {
  autoSaveIntervalMs: 30000, // 30 秒
  storageKeyPrefix: 'linkode_editor_backup_',
  maxBackups: 10,
};

// ============================================================================
// BackupManager Class
// ============================================================================

/**
 * 編輯器備份管理器
 *
 * 負責自動備份編輯器內容到 localStorage，並在需要時恢復
 * @see Requirements 10.3 - 每 30 秒自動備份
 * @see Requirements 10.4 - 錯誤恢復時還原游標位置
 */
export class BackupManager {
  private config: BackupManagerConfig;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private currentDocId: string | null = null;
  private getContent:
    | (() => { markdown: string; selection: EditorSelection; versionVector: number })
    | null = null;

  constructor(config: Partial<BackupManagerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * 取得 localStorage key
   */
  private getStorageKey(docId: string): string {
    return `${this.config.storageKeyPrefix}${docId}`;
  }

  /**
   * 檢查 localStorage 是否可用
   */
  private isStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 儲存備份到 localStorage
   */
  saveBackup(backup: EditorBackup): boolean {
    if (!this.isStorageAvailable()) {
      console.warn('[BackupManager] localStorage 不可用，無法儲存備份');
      return false;
    }

    try {
      const key = this.getStorageKey(backup.docId);
      const data = JSON.stringify(backup);
      localStorage.setItem(key, data);

      // 清理舊備份
      this.cleanupOldBackups();

      return true;
    } catch (error) {
      console.error('[BackupManager] 儲存備份失敗:', error);
      return false;
    }
  }

  /**
   * 從 localStorage 讀取備份
   */
  loadBackup(docId: string): EditorBackup | null {
    if (!this.isStorageAvailable()) {
      return null;
    }

    try {
      const key = this.getStorageKey(docId);
      const data = localStorage.getItem(key);

      if (!data) {
        return null;
      }

      const backup = JSON.parse(data) as EditorBackup;

      // 驗證備份資料結構
      if (!this.isValidBackup(backup)) {
        console.warn('[BackupManager] 備份資料格式無效，已忽略');
        return null;
      }

      return backup;
    } catch (error) {
      console.error('[BackupManager] 讀取備份失敗:', error);
      return null;
    }
  }

  /**
   * 刪除備份
   */
  removeBackup(docId: string): boolean {
    if (!this.isStorageAvailable()) {
      return false;
    }

    try {
      const key = this.getStorageKey(docId);
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('[BackupManager] 刪除備份失敗:', error);
      return false;
    }
  }

  /**
   * 檢查是否有備份
   */
  hasBackup(docId: string): boolean {
    return this.loadBackup(docId) !== null;
  }

  /**
   * 驗證備份資料結構
   */
  private isValidBackup(backup: unknown): backup is EditorBackup {
    if (!backup || typeof backup !== 'object') {
      return false;
    }

    const b = backup as Record<string, unknown>;

    return (
      typeof b.docId === 'string' &&
      typeof b.markdown === 'string' &&
      typeof b.timestamp === 'number' &&
      typeof b.versionVector === 'number' &&
      b.selection !== null &&
      typeof b.selection === 'object'
    );
  }

  /**
   * 清理舊備份 (保留最新的 maxBackups 個)
   */
  private cleanupOldBackups(): void {
    if (!this.isStorageAvailable()) {
      return;
    }

    try {
      const prefix = this.config.storageKeyPrefix;
      const backups: Array<{ key: string; timestamp: number }> = [];

      // 收集所有備份
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const backup = JSON.parse(data) as EditorBackup;
              backups.push({ key, timestamp: backup.timestamp });
            }
          } catch {
            // 忽略無效的備份
          }
        }
      }

      // 如果超過最大數量，刪除最舊的
      if (backups.length > this.config.maxBackups) {
        backups.sort((a, b) => b.timestamp - a.timestamp);
        const toRemove = backups.slice(this.config.maxBackups);

        for (const { key } of toRemove) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('[BackupManager] 清理舊備份失敗:', error);
    }
  }

  /**
   * 啟動自動備份
   */
  startAutoBackup(
    docId: string,
    getContent: () => { markdown: string; selection: EditorSelection; versionVector: number }
  ): void {
    // 停止之前的自動備份
    this.stopAutoBackup();

    this.currentDocId = docId;
    this.getContent = getContent;

    // 立即執行一次備份
    this.performAutoBackup();

    // 設定定時備份
    this.intervalId = setInterval(() => {
      this.performAutoBackup();
    }, this.config.autoSaveIntervalMs);
  }

  /**
   * 停止自動備份
   */
  stopAutoBackup(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.currentDocId = null;
    this.getContent = null;
  }

  /**
   * 執行自動備份
   */
  private performAutoBackup(): void {
    if (!this.currentDocId || !this.getContent) {
      return;
    }

    try {
      const { markdown, selection, versionVector } = this.getContent();

      const backup: EditorBackup = {
        docId: this.currentDocId,
        markdown,
        selection,
        versionVector,
        timestamp: Date.now(),
      };

      // Backup success is silent in production - only errors are logged
      this.saveBackup(backup);
    } catch (error) {
      console.error('[BackupManager] 自動備份失敗:', error);
    }
  }

  /**
   * 取得所有備份的文件 ID 列表
   */
  getAllBackupDocIds(): string[] {
    if (!this.isStorageAvailable()) {
      return [];
    }

    const prefix = this.config.storageKeyPrefix;
    const docIds: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const docId = key.slice(prefix.length);
        docIds.push(docId);
      }
    }

    return docIds;
  }

  /**
   * 清除所有備份
   */
  clearAllBackups(): void {
    if (!this.isStorageAvailable()) {
      return;
    }

    const docIds = this.getAllBackupDocIds();
    for (const docId of docIds) {
      this.removeBackup(docId);
    }
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * 全域備份管理器實例
 */
export const backupManager = new BackupManager();

// ============================================================================
// React Hook
// ============================================================================

import { useEffect, useCallback, useRef } from 'react';

/**
 * useEditorBackup Hook
 *
 * 提供編輯器自動備份功能的 React Hook
 */
export interface UseEditorBackupOptions {
  /** 文件 ID */
  docId: string;
  /** 是否啟用自動備份 */
  enabled?: boolean;
  /** 自動備份間隔 (毫秒) */
  intervalMs?: number;
  /** 取得當前內容的函式 */
  getContent: () => { markdown: string; selection: EditorSelection; versionVector: number };
}

export interface UseEditorBackupReturn {
  /** 手動儲存備份 */
  saveBackup: () => boolean;
  /** 讀取備份 */
  loadBackup: () => EditorBackup | null;
  /** 刪除備份 */
  removeBackup: () => boolean;
  /** 是否有備份 */
  hasBackup: boolean;
}

export function useEditorBackup(options: UseEditorBackupOptions): UseEditorBackupReturn {
  const { docId, enabled = true, intervalMs = 30000, getContent } = options;

  const managerRef = useRef<BackupManager | null>(null);
  const hasBackupRef = useRef(false);

  // 初始化備份管理器
  useEffect(() => {
    managerRef.current = new BackupManager({
      autoSaveIntervalMs: intervalMs,
    });

    // 檢查是否有備份
    hasBackupRef.current = managerRef.current.hasBackup(docId);

    return () => {
      managerRef.current?.stopAutoBackup();
    };
  }, [docId, intervalMs]);

  // 啟動/停止自動備份
  useEffect(() => {
    if (!managerRef.current) return;

    if (enabled) {
      managerRef.current.startAutoBackup(docId, getContent);
    } else {
      managerRef.current.stopAutoBackup();
    }

    return () => {
      managerRef.current?.stopAutoBackup();
    };
  }, [docId, enabled, getContent]);

  const saveBackup = useCallback((): boolean => {
    if (!managerRef.current) return false;

    const { markdown, selection, versionVector } = getContent();
    const backup: EditorBackup = {
      docId,
      markdown,
      selection,
      versionVector,
      timestamp: Date.now(),
    };

    const success = managerRef.current.saveBackup(backup);
    if (success) {
      hasBackupRef.current = true;
    }
    return success;
  }, [docId, getContent]);

  const loadBackupFn = useCallback((): EditorBackup | null => {
    return managerRef.current?.loadBackup(docId) ?? null;
  }, [docId]);

  const removeBackupFn = useCallback((): boolean => {
    const success = managerRef.current?.removeBackup(docId) ?? false;
    if (success) {
      hasBackupRef.current = false;
    }
    return success;
  }, [docId]);

  return {
    saveBackup,
    loadBackup: loadBackupFn,
    removeBackup: removeBackupFn,
    hasBackup: hasBackupRef.current,
  };
}
